/**
 * Customer 模块 - 常量定义
 * 
 * @description
 * 本文件定义 Customer 模块的常量，主要用于枚举值的中文标签映射。
 * 
 * @module Types/Customer/Constants
 * 
 * @backend-mapping
 * - CUSTOMER_TYPE_LABELS → backend/app/Modules/Sales/Models/Customer.php::getCustomerTypeTextAttribute()
 * - CREDIT_LEVEL_LABELS → backend/app/Modules/Sales/Models/Customer.php::getCreditLevelTextAttribute()
 * - CUSTOMER_STATUS_LABELS → 通用状态
 * 
 * @update-trigger
 * - Model 的标签映射方法变更时
 * - 枚举值的中文名称变更时
 * 
 * @validation-method
 * - 对照 Model 的文本方法
 * - 确保枚举值与标签一一对应
 * 
 * @author YSFL Team
 * @last-updated 2025-10-19
 */

import { CustomerType, CreditLevel, CustomerStatus } from './enums';

/**
 * 客户类型标签映射
 * @backend Customer::getCustomerTypeTextAttribute()
 */
export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  [CustomerType.DIRECT]: '直接客户',
  [CustomerType.AGENT]: '代理商',
  [CustomerType.OEM]: 'OEM',
};

/**
 * 信用等级标签映射
 * @backend Customer::getCreditLevelTextAttribute()
 */
export const CREDIT_LEVEL_LABELS: Record<CreditLevel, string> = {
  [CreditLevel.EXCELLENT]: '优',
  [CreditLevel.GOOD]: '良',
  [CreditLevel.AVERAGE]: '中',
  [CreditLevel.POOR]: '差',
  [CreditLevel.BLACKLIST]: '黑名单',
};

/**
 * 客户状态标签映射
 */
export const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
  [CustomerStatus.INACTIVE]: '停用',
  [CustomerStatus.ACTIVE]: '正常',
};

/**
 * 客户类型选项（用于下拉框）
 */
export const CUSTOMER_TYPE_OPTIONS = [
  { value: CustomerType.DIRECT, label: CUSTOMER_TYPE_LABELS[CustomerType.DIRECT] },
  { value: CustomerType.AGENT, label: CUSTOMER_TYPE_LABELS[CustomerType.AGENT] },
  { value: CustomerType.OEM, label: CUSTOMER_TYPE_LABELS[CustomerType.OEM] },
];

/**
 * 信用等级选项（用于下拉框）
 */
export const CREDIT_LEVEL_OPTIONS = [
  { value: CreditLevel.EXCELLENT, label: CREDIT_LEVEL_LABELS[CreditLevel.EXCELLENT] },
  { value: CreditLevel.GOOD, label: CREDIT_LEVEL_LABELS[CreditLevel.GOOD] },
  { value: CreditLevel.AVERAGE, label: CREDIT_LEVEL_LABELS[CreditLevel.AVERAGE] },
  { value: CreditLevel.POOR, label: CREDIT_LEVEL_LABELS[CreditLevel.POOR] },
  { value: CreditLevel.BLACKLIST, label: CREDIT_LEVEL_LABELS[CreditLevel.BLACKLIST] },
];

/**
 * 客户状态选项（用于下拉框）
 */
export const CUSTOMER_STATUS_OPTIONS = [
  { value: CustomerStatus.ACTIVE, label: CUSTOMER_STATUS_LABELS[CustomerStatus.ACTIVE] },
  { value: CustomerStatus.INACTIVE, label: CUSTOMER_STATUS_LABELS[CustomerStatus.INACTIVE] },
];

/**
 * ⚠️ 客户权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * - PERM_SALES_CUSTOMER_VIEW
 * - PERM_SALES_CUSTOMER_CREATE
 * - PERM_SALES_CUSTOMER_UPDATE
 * - PERM_SALES_CUSTOMER_DELETE
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

